import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  useSidebar,
} from '@/components/ui/sidebar';
import { useTranslation } from '@/hooks/useTranslation';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { NavigationItem } from './NavigationItem';
import type { NavigationGroup as NavigationGroupType } from './types';
import { createNavGroupId } from './utils';

interface NavigationGroupProps {
  group: NavigationGroupType;
  isActive: (path: string) => boolean;
  isGroupDefaultOpen: (group: NavigationGroupType) => boolean;
}

export const NavigationGroup = ({
  group,
  isActive,
  isGroupDefaultOpen,
}: NavigationGroupProps) => {
  const { tDynamic } = useTranslation();
  const { state, isMobile, setOpen: setSidebarOpen } = useSidebar();
  const groupId = createNavGroupId(group.label);
  const defaultOpen = group.defaultOpen ?? isGroupDefaultOpen(group);
  // Controlled open state so a collapsed-rail click can force THIS group open
  // (in addition to expanding the sidebar), not merely toggle it.
  const [groupOpen, setGroupOpen] = useState(defaultOpen);
  const translatedLabel = tDynamic(group.label);

  // When the sidebar is collapsed to the icon rail the submenu is hidden, so
  // toggling the collapsible does nothing visible. Instead, clicking a parent
  // expands the sidebar AND opens that specific group's submenu. Desktop
  // icon-rail only.
  const isIconCollapsed = state === 'collapsed' && !isMobile;

  return (
    <Collapsible
      asChild
      open={groupOpen}
      onOpenChange={setGroupOpen}
      className='group/collapsible'
    >
      <SidebarMenuItem>
        <CollapsibleTrigger
          asChild
          onClick={
            isIconCollapsed
              ? event => {
                  // Don't let the collapsible toggle (it would close an
                  // already-open group). Expand the sidebar and force this
                  // group open so its submenu is revealed.
                  event.preventDefault();
                  setSidebarOpen(true);
                  setGroupOpen(true);
                }
              : undefined
          }
        >
          <SidebarMenuButton
            tooltip={translatedLabel}
            aria-expanded={groupOpen}
            aria-controls={groupId}
          >
            {group.icon && <group.icon />}
            <span>{translatedLabel}</span>
            <ChevronRight className='ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub id={groupId}>
            {group.items.map(item => (
              <NavigationItem
                key={item.name}
                item={item}
                isActive={isActive}
                isSubItem
              />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};
